import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { assertIsTruthy } from '@llx/common';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CaseClusterFieldsFragment,
  CaseClusterRelationClientsFragment,
  useCreateCaseClusterMutation,
  useListClientsQuery,
  useListPatientCasesQuery,
  useUpdateCaseClusterMutation,
} from '@/@generated/codegen/API';
import { AppSpinner } from '@/ui/AppSpinner';
import ItemList from '@/ui/ItemList/ItemList';
import { useFormFieldArrayHelpers } from '@/utils/hooks/useFormFieldArrayHelpers';

type Props = {
  data: (CaseClusterFieldsFragment & CaseClusterRelationClientsFragment) | null;
  closeForm: () => void;
};

type FormData = {
  name: string;
  clientIDs: string[];
  selectedClientID: string | null;
  patientCaseIds: string[];
  selectedCaseID: string | null;
};

const CreateCaseClusterForm = ({ data, closeForm }: Props) => {
  const isEdit = !!data;
  const clients = useListClientsQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
    },
  });
  const clientList = useMemo(() => {
    return clients.data?.listClients.edges.map((el) => el.node) ?? [];
  }, [clients.data]);
  const patientCases = useListPatientCasesQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {},
    },
  });
  const patientCaseList = useMemo(() => {
    return patientCases.data?.listPatientCases.edges.map((el) => el.node) ?? [];
  }, [patientCases.data]);
  const { t } = useTranslation();
  const toast = useToast();
  const [mutationCreateCaseCluster] = useCreateCaseClusterMutation();
  const [mutationUpdateCaseCluster] = useUpdateCaseClusterMutation({});

  const form = useForm<FormData>({
    defaultValues: {
      selectedCaseID: undefined,
      patientCaseIds: [],
      selectedClientID: undefined,
      clientIDs: [],
      name: '',
    },
  });
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = form;

  const caseListOfSelectedClients = useMemo(() => {
    if (!patientCases.data || !clients.data) {
      return [];
    }
    const currentClientIDs = watch('clientIDs') ?? [];
    return (
      patientCaseList.filter((caseItem) =>
        currentClientIDs.some((selectedClientID) => {
          const client = clientList.find((client) => client.id === selectedClientID)!;

          return caseItem.clientName === client.name;
        })
      ) ?? []
    );
  }, [patientCaseList, clientList, watch('clientIDs')]);

  const {
    handleAdd: handleAddClient,
    handleRemove: handleRemoveClient,
    getAvailableItems: getAvailableClients,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'clientIDs',
    selectorFieldName: 'selectedClientID',
    allElements: clientList ?? [],
    onRemoveInterceptor: (id, currentArrayElements, expectedNewArray) => {
      if (!patientCases.data || !clients.data) {
        // NOTE: not done loading, so don't allow the update
        return currentArrayElements;
      }
      const client = clientList.find((client) => client.id === id)!;
      const currentPatientCaseIDs = form.getValues('patientCaseIds') ?? [];
      const cases = currentPatientCaseIDs.map((caseId) =>
        patientCaseList.find((caseItem) => caseItem.id === caseId)
      );
      const casesPartOfClientGettingRemoved = cases.filter((caseItem) => {
        return caseItem?.clientName === client.name;
      });

      if (isEdit) {
        // NOTE: only allow removing a client if there are no cases of that client in the caseIDs array
        if (casesPartOfClientGettingRemoved.length > 0) {
          toast({
            title: 'Cannot remove client',
            description: 'There are cases of this client in the case cluster.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return currentArrayElements;
        }
      }
      // NOTE: if the user removes a client, we remove the cases of that client from the caseIDs array
      const patientCaseIDsToKeep = currentPatientCaseIDs.filter((id) =>
        casesPartOfClientGettingRemoved.every((caseItem) => caseItem?.id !== id)
      );
      form.setValue('patientCaseIds', patientCaseIDsToKeep);
      return expectedNewArray;
    },
  });
  const {
    handleAdd: handleAddCase,
    handleRemove: handleRemoveCase,
    getAvailableItems: getAvailableCases,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'patientCaseIds',
    selectorFieldName: 'selectedCaseID',
    allElements: caseListOfSelectedClients ?? [],
  });

  const currentClientIDs = watch('clientIDs') ?? [];
  const currentPatientCaseIds = watch('patientCaseIds') ?? [];

  // --------------------- handlers ------------------------

  const onSubmit = async (values: FormData) => {
    if (isEdit) {
      assertIsTruthy(data);
      await mutationUpdateCaseCluster({
        variables: {
          data: {
            id: data.id,
            name: {
              set: values.name,
            },
            clientIds: values.clientIDs,
          },
        },
      });
      toast({
        title: t('admin.caseClusters.updated'),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      await mutationCreateCaseCluster({
        variables: {
          data: {
            name: values.name,
            patientCaseIds: values.patientCaseIds,
            clientIds: values.clientIDs,
          },
        },
      });
      toast({
        title: t('admin.caseClusters.created'),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
    closeForm();
    reset();
  };

  const onClientChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedClientID', e.target.value);
      handleAddClient();
      form.setValue('selectedClientID', '');
    },
    [form, handleAddClient]
  );

  const onCaseChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedCaseID', e.target.value);
      handleAddCase();
      form.setValue('selectedCaseID', '');
    },
    [form, handleAddCase]
  );

  // --------------------- effects ------------------------

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        clientIDs: data.clients.edges.map((el) => el.node.id) || [],
        patientCaseIds: data.cases.edges.map((el) => el.node.id) || [],
      });
    }
  }, [data]);

  // NOTE: when data changes, set the name of the case cluster
  useEffect(() => {
    if (!clients.data || !patientCases.data) {
      return;
    }
    const selectedClients = currentClientIDs.map((clientId) =>
      clientList.find((elem) => elem.id === clientId)
    );
    const selectedCases = currentPatientCaseIds.map((caseId) =>
      patientCaseList.find((elem) => elem.id === caseId)
    );
    form.setValue(
      'name',
      `${selectedClients.map((el) => el?.name).join(', ')} - ${selectedCases.map((el) => el?.name).join(', ')}`
    );
  }, [currentClientIDs, currentPatientCaseIds]);

  if (!clients.data || !patientCases.data) {
    return <AppSpinner />;
  }

  // --------------------- render ------------------------
  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
    >
      <VStack gap="10px" pb="10px">
        {/* NAME */}
        <FormControl isRequired isInvalid={Boolean(errors.name)} isDisabled={true}>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.name')}:
          </FormLabel>
          <Input type="text" {...register('name', { required: true })} placeholder="Name" />
          {errors.name && (
            <FormHelperText color="red">
              {t('admin.caseClusters.createForm.name.error')}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.clients')}
          </FormLabel>

          <HStack>
            <Controller
              name="selectedClientID"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder={
                    t('dmin.caseClusters.createForm.field.clients.placeholder') || 'Select'
                  }
                  {...field}
                  value={field.value ?? undefined}
                  onChange={onClientChange}
                >
                  {getAvailableClients().map((elem) => (
                    <option key={elem.id} value={elem.id}>
                      {elem.name}
                    </option>
                  ))}
                </Select>
              )}
            />
          </HStack>

          <VStack p={2} align="flex-start">
            {currentClientIDs.length && (
              <ItemList
                itemList={currentClientIDs.map((clientId) => {
                  const elem = clientList?.find((elem) => elem.id === clientId);
                  return {
                    id: clientId,
                    value: elem?.name || 'N/A',
                  };
                })}
                removeItemFunc={handleRemoveClient}
              />
            )}
          </VStack>
        </FormControl>

        {/* CASE LIST */}

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="normal" mb="1">
            {t('admin.caseClusters.createForm.field.cases')}
          </FormLabel>

          <HStack>
            <Controller
              name="selectedCaseID"
              control={control}
              disabled={isEdit}
              render={({ field }) => (
                <Select
                  placeholder={
                    t('dmin.caseClusters.createForm.field.cases.placeholder') || 'Select'
                  }
                  {...field}
                  value={field.value ?? undefined}
                  onChange={onCaseChange}
                  isDisabled={isEdit}
                >
                  {getAvailableCases().map((elem) => (
                    <option key={elem.id} value={elem.id}>
                      {elem.name} (ID: {elem.legacyCaseId})
                    </option>
                  ))}
                </Select>
              )}
            />
          </HStack>

          <VStack p={2} align="flex-start">
            {currentPatientCaseIds.length && (
              <ItemList
                itemList={currentPatientCaseIds.map((patientCaseId) => {
                  const elem = patientCaseList.find((elem) => elem.id === patientCaseId);
                  return {
                    id: patientCaseId,
                    value: elem?.name || 'N/A',
                  };
                })}
                removeItemFunc={handleRemoveCase}
                disableAllRemove={isEdit}
              />
            )}
          </VStack>
        </FormControl>
      </VStack>

      {/* SUBMIT */}
      <Button
        isDisabled={currentPatientCaseIds.length < 1 || currentClientIDs.length < 1}
        onClick={() => {
          void handleSubmit(onSubmit)();
        }}
        p="2"
        mx="2"
        w="20%"
        background="blue.400"
        color="white"
        variant="solid"
      >
        {isEdit ? t('common.update') : t('common.create')}
      </Button>
    </form>
  );
};

export default CreateCaseClusterForm;
